import _ from 'underscore';
import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Link } from '@fluentui/react';
import { keyCode } from '@bingads-webui/keycode';

import { TARGETING_TYPE } from '../util/targeting-type';
import { locationPropType } from '../util/index';
import useLocationTargetDescription from '../util/use-location-target-description';
import { LocationTargetName } from '../location-target-name';

export function SearchResult(props) {
  const {
    isAlreadyTargeted,
    isExcludeEnabled,
    isIncludeEnabled,
    isRadiusExcludeEnabled,
    isRadiusTarget,
    location,
    radiusValue,
    radiusUnit,
    onLocationTargetInclude,
    onLocationTargetExclude,
    showExcludeButton,
    showIncludeButton,
    targetingType,
    instrumentationService,
    closeSearchResultDropdown,
    showExcludeButtonIgnoreLocationId,
  } = props;

  const i18n = useContext(I18nContext);

  const handleLocationTargetInclude = useCallback(() => {
    onLocationTargetInclude(location);
  }, [location, onLocationTargetInclude]);

  const handleLocationTargetExclude = useCallback(() => {
    if (onLocationTargetExclude) {
      onLocationTargetExclude(location);
    }
  }, [location, onLocationTargetExclude]);

  const onIncludeButtonKeyDown = useCallback((event) => {
    if (event.keyCode === keyCode.Escape) {
      closeSearchResultDropdown();
      event.preventDefault();
      event.stopPropagation();
    } else if (event.keyCode === keyCode.Enter || event.keyCode === keyCode.Space) {
      handleLocationTargetInclude();
    }
  }, [closeSearchResultDropdown, handleLocationTargetInclude]);

  const onExcludeButtonKeyDown = useCallback((event) => {
    if (event.keyCode === keyCode.Escape) {
      closeSearchResultDropdown();
      event.preventDefault();
      event.stopPropagation();
    } else if (event.keyCode === keyCode.Enter || event.keyCode === keyCode.Space) {
      handleLocationTargetExclude();
    }
  }, [closeSearchResultDropdown, handleLocationTargetExclude]);

  let excludeButtonAndSeparator = null;
  if (showExcludeButton && (location.Id || showExcludeButtonIgnoreLocationId)
    && (!isRadiusTarget || (isRadiusTarget && isRadiusExcludeEnabled))) {
    excludeButtonAndSeparator = (
      <React.Fragment>
        {showIncludeButton && ' | '}
        <Link
          className="search-result-exclude-link"
          onClick={handleLocationTargetExclude}
          disabled={!isExcludeEnabled}
          onKeyDown={onExcludeButtonKeyDown}
        >
          {i18n.getString(_TL_('Exclude'))}
        </Link>
      </React.Fragment>
    );
  }

  const targetAriaLabel = useLocationTargetDescription(
    location,
    targetingType,
    radiusValue,
    radiusUnit,
    isRadiusTarget
  );

  return (
    <div className="bing-maps-search-box-search-result">
      <div className="bing-maps-search-box-search-result-name">
        <LocationTargetName
          isRadiusTarget={isRadiusTarget}
          location={location}
          radiusUnit={radiusUnit}
          radiusValue={radiusValue}
          instrumentationService={instrumentationService}
        />
      </div>
      <div className="bing-maps-search-box-search-result-buttons">
        {!isAlreadyTargeted ? (
          <React.Fragment>
            {showIncludeButton &&
              <Link
                className="search-result-include-link"
                onClick={handleLocationTargetInclude}
                disabled={!isIncludeEnabled}
                aria-label={targetAriaLabel}
                onKeyDown={onIncludeButtonKeyDown}
              >
                {i18n.getString(_TL_('Target'))}
              </Link>
            }
            {excludeButtonAndSeparator}
          </React.Fragment>
        ) :
          (
            <span className="bing-maps-search-box-search-result-already-exists">
              {i18n.getString(targetingType === 'Excluded' ? _TL_('Excluded') : _TL_('Targeted'))}
            </span>
          )}
      </div>
    </div>
  );
}

SearchResult.propTypes = {
  isAlreadyTargeted: PropTypes.bool.isRequired,
  isExcludeEnabled: PropTypes.bool.isRequired,
  isIncludeEnabled: PropTypes.bool.isRequired,
  isRadiusExcludeEnabled: PropTypes.bool.isRequired,
  isRadiusTarget: PropTypes.bool.isRequired,
  location: locationPropType.isRequired,
  onLocationTargetInclude: PropTypes.func.isRequired,
  onLocationTargetExclude: PropTypes.func,
  showExcludeButton: PropTypes.bool.isRequired,
  showIncludeButton: PropTypes.bool,
  radiusUnit: PropTypes.string.isRequired,
  radiusValue: PropTypes.number.isRequired,
  targetingType: PropTypes.oneOf(_.values(TARGETING_TYPE)).isRequired,
  instrumentationService: PropTypes.shape({
    logError: PropTypes.func.isRequired,
    logTrace: PropTypes.func.isRequired,
  }),
  closeSearchResultDropdown: PropTypes.func.isRequired,
  showExcludeButtonIgnoreLocationId: PropTypes.bool,
};

SearchResult.defaultProps = {
  onLocationTargetExclude: null,
  instrumentationService: { logError: _.noop, logTrace: _.noop },
  showIncludeButton: true,
  showExcludeButtonIgnoreLocationId: false,
};
